import React, { useState } from "react";
import { Warehouse as WHIcon, MapPin, Search, Plus, Layers, Navigation } from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { WarehouseMap } from "./WarehouseMap";
import { formatCurrency } from "../../utils/formatters";

export function WarehouseModule() {
  const { warehouses, products, batches } = useApp();
  const [selectedWhId, setSelectedWhId] = useState(warehouses[0]?.id || "");
  const [locateQuery, setLocateQuery] = useState("");

  const activeWarehouse = warehouses.find((w) => w.id === selectedWhId) || warehouses[0];

  // Locate product exact path
  const locateResults = React.useMemo(() => {
    if (!locateQuery.trim() || locateQuery.length < 2) return [];
    const q = locateQuery.toLowerCase();
    const matchedProducts = products.filter((p) => p.name.toLowerCase().includes(q) || p.barcode.includes(q));

    const paths = [];
    matchedProducts.forEach((prod) => {
      const prodBatches = batches.filter((b) => b.productId === prod.id && b.status !== "Expired");
      prodBatches.forEach((b) => {
        const wh = warehouses.find((w) => w.id === b.warehouseId);
        paths.push({
          product: prod,
          warehouseName: wh?.name,
          binCode: b.binCode,
          batchNo: b.batchNo,
          qtyRemaining: b.qtyRemaining
        });
      });
    });
    return paths;
  }, [locateQuery, products, batches, warehouses]);

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <WHIcon className="w-5 h-5 text-emerald-500" />
            Warehouse Exact Bin-Level Hierarchy & Interactive Map
          </h2>
          <p className="text-xs text-slate-500">
            Visual Section → Rack → Shelf → Bin hierarchy with live occupancy heatmaps & product navigation
          </p>
        </div>

        {/* Warehouse Selector Tabs */}
        <div className="flex items-center gap-2 p-1 bg-slate-100 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-xs">
          {warehouses.map((wh) => (
            <button
              key={wh.id}
              onClick={() => setSelectedWhId(wh.id)}
              className={`px-3 py-1.5 rounded-lg font-semibold transition ${
                wh.id === activeWarehouse?.id
                  ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {wh.name.split("(")[0]}
            </button>
          ))}
        </div>
      </div>

      {/* "Locate Product" Instant Path Finder */}
      <div className="p-4 rounded-2xl border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/40 dark:bg-emerald-950/20 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-900 dark:text-white">
            <Navigation className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            Exact Path Locator: &ldquo;Where is it and how much?&rdquo;
          </div>
          <span className="text-[10px] font-mono text-emerald-600 dark:text-emerald-400">
            Instant Shelf Navigation
          </span>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={locateQuery}
            onChange={(e) => setLocateQuery(e.target.value)}
            placeholder="Type any grocery item (e.g. Basmati Rice, Sunflower Oil, Toor Dal)..."
            className="w-full pl-9 pr-3 py-2 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          />
        </div>

        {locateResults.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 pt-1 text-xs">
            {locateResults.map((res, idx) => (
              <div
                key={idx}
                className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col justify-between"
              >
                <div>
                  <p className="font-bold text-slate-900 dark:text-white">{res.product.name}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{res.warehouseName}</p>
                </div>
                <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between font-mono">
                  <span className="bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 font-bold px-2 py-0.5 rounded text-[11px]">
                    📍 Bin: {res.binCode}
                  </span>
                  <span className="font-bold text-slate-800 dark:text-slate-200">
                    {res.qtyRemaining} {res.product.unit}s
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Interactive Map Visualizer for the Selected Warehouse */}
      {activeWarehouse && <WarehouseMap warehouse={activeWarehouse} />}
    </div>
  );
}
